const maxHeightMarginNum = 50
const maxHeightMargin = `${maxHeightMarginNum / 2}px`
const floatingPanelMaxHeight = `calc(100vh - ${maxHeightMargin})`
let visibilityObserver = null
let floatingPanelElement = null
let clickAwayListenerEvent = null
let cachedOverflow = null
let resizeObserver = null

function getOverflowElementExists(context) {
  if (!cachedOverflow) {
    cachedOverflow = Boolean(context.getFilesSidebar()?.Overflow?.node)
  }

  return cachedOverflow
}

function cleanup() {
  visibilityObserver?.disconnect?.()
  resizeObserver?.disconnect?.()
  if (clickAwayListenerEvent) {
    window.document.removeEventListener('click', clickAwayListenerEvent, {
      capture: true
    })
  }
  floatingPanelElement = null
  clickAwayListenerEvent = null
  visibilityObserver = null
  resizeObserver = null
}

/**
 * Sets up a floating panel at a given position relative to the component.
 *
 * The floating panel is a component that appears when the user hovers over a
 * component in the DynamicIsland. It displays additional information about the
 * component, such as its props, state, and context.
 *
 * @function setFloatingPanel
 * @param {Object} data The data to display in the floating panel.
 * @param {Object} opts Parameters for the floating panel.
 * @param {Object} opts.panelOffset The position offset of the floating panel
 * @param {Object} opts.customProps Custom Properties for Floating Panel
 */
export async function setFloatingPanel(data, opts = {}) {
  if (!data || this.call('isEmptyObject', data)) {
    if (floatingPanelElement) {
      floatingPanelElement?.setNodeStyles?.({
        visibility: 'hidden',
        opacity: '0'
      })
    }
    return
  }

  cleanup()
  floatingPanelElement = this.getFloatingPanelElement()
  const calleeElement = opts.callee || data?.element || this

  const calleeRect = calleeElement.node?.getBoundingClientRect?.() || {}

  clickAwayListenerEvent = (ev) => this.handleClickAway(ev)

  // this is needed to react to external visibility changes of floating panel eg.: handleClickAway placed at Canvas comp.
  visibilityObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target.style?.visibility === 'hidden') {
        cleanup()
      }
    }
  })

  resizeObserver = new ResizeObserver((entries) => {
    const [floatingPanelEntry] = entries
    if (calleeRect.top < floatingPanelEntry.contentRect.height / 2) {
      floatingPanelElement.node.style.transform = `translateY(-${floatingPanelEntry.target.offsetTop - maxHeightMarginNum / 2}px)`
    }
  })

  visibilityObserver.observe(floatingPanelElement.node, {
    attributes: true,
    attributeFilter: ['style']
  })

  resizeObserver.observe(floatingPanelElement.node)

  const calleeLeftPos = calleeRect.left || 0
  const calleeWidth = calleeRect.width || 0
  const floatingPanelLeftPos = calleeLeftPos + calleeWidth

  const updateStyles = (styles) => {
    floatingPanelElement?.setNodeStyles?.({
      ...styles
    })

    if (opts.customProps) {
      floatingPanelElement.update(opts.customProps)
    }
  }

  const updateSidebarItem = async (calleeItem, isArtboard) => {
    if (isArtboard) {
      await this.call(
        'updateItemInSidebar',
        calleeItem.type,
        calleeItem.key,
        opts
      )
    } else {
      const type = calleeItem?.type || floatingPanelElement.state.type
      await this.call('rerenderFileSidebarByType', type)
    }
  }

  // State management
  const updateSceneState = async (
    newData,
    calleeItemState,
    isArtboard,
    options = {}
  ) => {
    if (floatingPanelElement?.state?.type !== newData?.type) {
      options.preventUpdateAfterCount = 1
    }

    floatingPanelElement.state.set(newData, options)

    if (calleeItemState) {
      await updateSidebarItem(calleeItemState, isArtboard)
    }
  }

  const showPanel = () => {
    window.document.addEventListener('click', clickAwayListenerEvent, {
      capture: true
    })
    if (!floatingPanelElement) {
      return
    }
    floatingPanelElement.data.isActive = true

    const allowDropdownOverflow =
      floatingPanelElement.state?.type === 'semantic_color'

    const top = `${calleeRect.top + calleeRect.height}px`

    updateStyles(
      {
        opacity: '1',
        visibility: 'visible',
        position: 'absolute',
        transform: 'translateY(-50%)',
        top,
        overflow: allowDropdownOverflow ? 'visible' : 'auto',
        maxHeight: floatingPanelMaxHeight,
        left: `${floatingPanelLeftPos}px`
      },
      opts
    )
  }

  const overflowExists = getOverflowElementExists(this)

  if (!overflowExists || !floatingPanelElement) {
    console.warn('Required components not found for FloatingPanel')
    return
  }

  try {
    if (this.node) {
      const calleeItemState =
        calleeElement.lookup((el, s) => el.Rename && el.ContextMenu && s.key)
          ?.state || null

      const isArtboard =
        calleeItemState && this.call('hasArtboard', calleeItemState?.type)
      await updateSceneState(data, calleeItemState, isArtboard)
    }

    showPanel(this)
  } catch (error) {
    console.error('Error setting up floating panel:', error)
  }
}

async function handlePanelKeyUp(event) {
  if (
    event.keyCode !== 46 ||
    !this.state.type ||
    (this.state.type === 'global_theme' &&
      (this.state.key === '@light' || this.state.key === '@dark')) ||
    (this.state.type === 'theme' && this.state.key === 'document')
  ) {
    return
  }

  await this.updateDesignSystem(
    `${(this.state.dsType || this.state.type).toUpperCase()}.${this.state.key}`,
    null
  )
  await this.call('setFloatingPanel')
}

let panelAuxFn = () => null

export function handlePanelDelete() {
  document.body.removeEventListener('keyup', panelAuxFn)
  panelAuxFn = () => null

  if (!this.call('isEmptyObject', this.state)) {
    panelAuxFn = handlePanelKeyUp.bind(this)
    document.body.addEventListener('keyup', panelAuxFn)
  }
}
